import BottomNav from './BottomNav'
import { IconHome } from './icons'

/**
 * Support & FAQ page.
 * Rendered as an in-app screen (via gameState 'support') and as a public URL
 * (/support) — when accessed via URL, onBack is undefined and the BottomNav
 * is hidden (same conditional as the old floating back arrow).
 *
 * FAQ items use <details>/<summary> for native collapsible behaviour.
 */
function FAQSection({ title, items }) {
  return (
    <div className="support-page__section">
      <h2 className="support-page__section-title">{title}</h2>
      {items.map(({ q, a }) => (
        <details key={q} className="support-page__faq-item">
          <summary className="support-page__faq-q">{q}</summary>
          <div className="support-page__faq-a">{a}</div>
        </details>
      ))}
    </div>
  )
}

const FAQ = [
  {
    title: 'Getting Started',
    items: [
      {
        q: 'What is Action! The Movie Game?',
        a: 'Action! is a movie connection game — connect two actors through shared film appearances in as few hops as possible. One hop = one movie + one actor. The fewer hops, the better your score.',
      },
      {
        q: 'How do I create an account?',
        a: 'Download the app and sign in with your email address. Your account is created automatically on first sign-in.',
      },
      {
        q: 'Is Action! free to play?',
        a: 'Yes — Sign up for a free account and receive 10 tickets to start, and you earn 1 free ticket every day by completing the Daily Challenge. Additional tickets can be purchased via in-app purchase.',
      },
    ],
  },
  {
    title: 'Tickets',
    items: [
      {
        q: 'What are tickets?',
        a: 'Tickets are the in-app currency used to play games. Most game modes cost 1 ticket per player.',
      },
      {
        q: 'How do I earn free tickets?',
        a: 'You earn 1 ticket per day by completing the Daily Challenge. Tickets are also awarded for maintaining your daily streak.',
      },
      {
        q: 'Can I get a refund on ticket purchases?',
        a: 'In-app purchases made through the iOS app are processed by Apple. Refund requests must be submitted directly through Apple (Settings → [Your Name] → Subscriptions → Action!). Purchases made on the web are processed by Stripe. For web purchase refunds, contact us at support@actionthemoviegame.com.',
      },
    ],
  },
  {
    title: 'Gameplay',
    items: [
      {
        q: 'What is a hop?',
        a: 'One hop = one movie + one connecting actor. Example: Tom Hanks → Cast Away → Helen Hunt. That\'s one hop.',
      },
      {
        q: 'What are Cue Cards?',
        a: 'Cue Cards are hints that help you find a connection. The temperature indicator shows how close your current pick is to the goal actor.',
      },
      {
        q: 'What is the Daily Challenge?',
        a: 'Every day a new puzzle is available to all players. Yesterday\'s goal actor becomes today\'s start actor, creating a continuous chain. Complete it to earn a free ticket and maintain your streak.',
      },
      {
        q: 'What are Hollywood Career themes?',
        a: 'Themes are visual styles that unlock based on your daily streak. The longer your streak, the more exclusive the theme you unlock — from Extra all the way to Oscar Night.',
      },
    ],
  },
  {
    title: 'Party Mode',
    items: [
      {
        q: 'How does Party Mode work?',
        a: 'Party Mode is designed for groups. One device acts as the host screen (ideal on a TV or laptop), and each player joins on their own phone. The host selects a game mode and controls the pace of the game.',
      },
      {
        q: 'Do all players need tickets for Party Mode?',
        a: 'Yes — each player is charged 1 ticket when the game begins.',
      },
      {
        q: 'What Party Mode games are available?',
        a: (
          <ul className="support-page__faq-list">
            <li><strong>Battle Royale</strong> — every player for themselves</li>
            <li><strong>Speed Run</strong> — fastest player wins</li>
            <li><strong>H2H</strong> — team vs team showdown</li>
            <li><strong>Co-Op</strong> — work together as one</li>
            <li><strong>Director's Cut</strong> — vote on each connection</li>
            <li><strong>The Trilogy</strong> — three rounds, one champion</li>
          </ul>
        ),
      },
    ],
  },
  {
    title: 'Account & Settings',
    items: [
      {
        q: 'How do I change my display name or avatar?',
        a: 'Go to Profile → tap your avatar to upload a new photo, or tap your display name to edit it.',
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Profile → scroll to the bottom → Delete Account. This action is permanent and will remove all your data, tickets, and game history.',
      },
      {
        q: 'I found a bug. How do I report it?',
        a: (
          <>
            Email us at{' '}
            <a href="mailto:support@actionthemoviegame.com" className="legal-page__link">
              support@actionthemoviegame.com
            </a>{' '}
            with a description of the issue, your device type, and what you were doing when it
            happened. We read every report.
          </>
        ),
      },
    ],
  },
]

export default function SupportPage({ onBack }) {
  return (
    <div className="legal-page support-page">
      <div className="screen-content legal-page__content">
        <h1 className="legal-page__title">Support &amp; FAQ</h1>
        <p className="legal-page__intro">Welcome to Action! Support — How can we help?</p>

        {FAQ.map((section) => (
          <FAQSection key={section.title} title={section.title} items={section.items} />
        ))}

        <div className="support-page__contact">
          <h2 className="support-page__contact-title">Contact Us</h2>
          <p className="support-page__contact-body">Still need help? We'd love to hear from you.</p>
          <a href="mailto:support@actionthemoviegame.com" className="support-page__contact-email">
            support@actionthemoviegame.com
          </a>
          <p className="support-page__contact-response">We typically respond within 1–2 business days.</p>
        </div>

        {onBack && (
          <BottomNav items={[
            { key: 'home', icon: IconHome, label: 'Home', variant: 'primary', onClick: onBack },
          ]} />
        )}
      </div>
    </div>
  )
}
